import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const HomePage = () => {
  const { language } = useLanguage();
  const t = translations.home;
  
  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section
        className={`relative flex items-center justify-center overflow-hidden ${
          isMobile ? 'min-h-[70vh]' : 'min-h-[calc(100vh-4rem)] md:min-h-screen'
        }`}
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7655107/pexels-photo-7655107.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll'
        }}
        data-testid="hero-section"
      >
        {/* Dark Overlay - Original treatment, consistent for mobile and desktop */}
        <div className="absolute inset-0 bg-primary/80"></div>
        
        {/* Additional localized gradient for mobile content area only - starts below header */}
        {isMobile && (
          <div className="absolute inset-x-0 top-16 bottom-0 bg-gradient-to-b from-transparent via-primary/10 to-primary/20"></div>
        )}

        {/* Content */}
        <div className={`relative z-10 container mx-auto px-5 md:px-6 lg:px-8 text-center ${
          isMobile ? 'py-6' : 'py-8 md:py-0'
        }`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            {/* Title */}
            <h1 className={`font-bold font-noto-serif text-white tracking-tight ${
              isMobile ? 'text-3xl mb-3' : 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 md:mb-6'
            }`} style={{ lineHeight: 1.375 }}>
              {t.heroTitle[language] || t.heroTitle.zh}
            </h1>
            
            {/* Subtitle */}
            <p className={`text-white/90 mx-auto leading-relaxed font-noto-sans ${
              isMobile ? 'text-base max-w-xs mb-5' : 'text-base md:text-lg lg:text-xl max-w-xl md:max-w-3xl mb-6 md:mb-8 px-2 md:px-0'
            }`}>
              {isMobile 
                ? (t.heroSubtitleMobile?.[language] || t.heroSubtitle[language])
                : (t.heroSubtitle[language] || t.heroSubtitle.zh)
              }
            </p>

            {/* CTA Buttons - Mobile: stacked layout, Desktop: single row with 4 buttons */}
            {isMobile ? (
              // Mobile Layout - Keep original stacked structure
              <>
                <div className="flex flex-col items-center justify-center gap-2.5 mb-4">
                  <Link
                    to="/services"
                    className="w-full bg-secondary text-primary px-8 py-3 rounded-md font-noto-sans font-semibold hover:bg-secondary/90 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
                    data-testid="cta-services"
                  >
                    {t.ctaServices[language]}
                    <ArrowRight size={18} />
                  </Link>
                  <Link
                    to="/contact"
                    className="w-full border-2 border-white text-white px-8 py-3 rounded-md font-noto-sans font-medium hover:bg-white hover:text-primary transition-all inline-flex items-center justify-center gap-2"
                    data-testid="cta-contact"
                  >
                    {t.ctaContact[language]}
                  </Link>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2.5 mb-5">
                  <Link
                    to="/about"
                    className="text-sm border border-white/40 text-white/90 px-5 py-2 rounded-md font-noto-sans font-medium hover:border-white hover:text-white transition-all"
                    data-testid="cta-about"
                  >
                    {t.ctaAbout[language]}
                  </Link>
                  <Link
                    to="/careers"
                    className="text-sm border border-secondary/50 text-secondary/90 px-5 py-2 rounded-md font-noto-sans font-medium hover:border-secondary hover:text-secondary transition-all"
                    data-testid="cta-careers"
                  >
                    {t.ctaCareers[language]}
                  </Link>
                </div>
              </>
            ) : (
              // Desktop Layout - Single row with 4 buttons, professional hierarchy
              <div className="flex items-center justify-center gap-3 lg:gap-4">
                {/* 服務項目 - Primary CTA (gold filled) */}
                <Link
                  to="/services"
                  className="bg-secondary text-primary px-7 lg:px-8 py-2.5 lg:py-3 rounded-md font-noto-sans font-semibold hover:bg-secondary/90 transition-all shadow-md hover:shadow-lg inline-flex items-center justify-center gap-2 text-sm lg:text-base"
                  data-testid="cta-services"
                >
                  {t.ctaServices[language]}
                  <ArrowRight size={16} />
                </Link>
                
                {/* 了解我們 - Neutral secondary (white outline) */}
                <Link
                  to="/about"
                  className="border-2 border-white/80 text-white px-6 lg:px-7 py-2.5 lg:py-3 rounded-md font-noto-sans font-medium hover:border-white hover:bg-white/10 transition-all text-sm lg:text-base"
                  data-testid="cta-about"
                >
                  {t.ctaAbout[language]}
                </Link>
                
                {/* 查看職缺 - Emphasized secondary (gold outline) */}
                <Link
                  to="/careers"
                  className="border-2 border-secondary/70 text-secondary px-6 lg:px-7 py-2.5 lg:py-3 rounded-md font-noto-sans font-medium hover:border-secondary hover:bg-secondary/10 transition-all text-sm lg:text-base"
                  data-testid="cta-careers"
                >
                  {t.ctaCareers[language]}
                </Link>
                
                {/* 聯絡我們 - Neutral secondary (white outline) */}
                <Link
                  to="/contact"
                  className="border-2 border-white/80 text-white px-6 lg:px-7 py-2.5 lg:py-3 rounded-md font-noto-sans font-medium hover:border-white hover:bg-white/10 transition-all text-sm lg:text-base"
                  data-testid="cta-contact"
                >
                  {t.ctaContact[language]}
                </Link>
              </div>
            )}

            {/* Business Hours */}
            <div className={`flex items-center justify-center ${isMobile ? '' : 'mt-8 md:mt-10'}`}>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
                <Clock size={14} className="text-secondary/80" />
                <span className="text-xs md:text-sm text-white/70 font-noto-sans">{t.businessHours[language]}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
